import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import { useTranslation, Trans } from "react-i18next";
import moment from "moment";

import { MediaQueries, Spacing } from "@paclife/plds.tokens";

import Logs from "assets/svg/logs.svg";
import { US_STATES_ABBREV } from "constants/index";

import Card from "common/Card/Card";
import Typography from "common/Typography/Typography";

import { getUserLogs } from "api/logs";
// import localData from "./localData.json";

const CustomGrid = styled.div`
    display: grid;
    @media (min-width: ${MediaQueries.md}) {
        grid-template-columns: repeat(2, minmax(150px, 1fr));
    }
    @media (min-width: ${MediaQueries.lg}) {
        grid-template-columns: repeat(3, minmax(150px, 1fr));
    }
    gap: 24px;
    margin-bottom: ${Spacing[24]};
`;

const Image = styled.img`
    width: 120px;
    display: none;

    @media (min-width: ${MediaQueries.lg}) {
        display: block;
        grid-column: 3 / 4;
        grid-row: 1 / 3;
        justify-self: center;
    }
`;

const shine = keyframes`
    to {
      background-position-x: -200%;
    }
`;

const Loading = styled.div`
    height: 200px;
    background: rgb(238, 238, 238);
    background: linear-gradient(110deg, #ececec 8%, #f5f5f5 18%, #ececec 33%);
    border-radius: 5px;
    background-size: 200% 100%;
    animation: 1.5s ${shine} linear infinite;
    grid-column: 1 / 3;
`;

const SubTitle = styled(Typography)`
    display: block;
    margin-bottom: 20px;
`;

const Table = styled.div`
    grid-column: 1 / 3;
`;

const Row = styled.div`
    display: grid;
    grid-template-columns: 150px 250px 120px 100px 100px;
    row-gap: 4px;

    ${({ content }) =>
        content &&
        css`
            height: 200px;
            overflow-y: scroll;
        `}
`;

const Header = styled(Typography)`
    display: block;
`;

const Cell = styled(Typography)`
    display: block;
`;

const RecentActivities = () => {
    const { t } = useTranslation("recentActivities");
    const [loading, setLoading] = useState(true);
    const [activities, setActivities] = useState([]);

    const formatActivities = (rawData) => {
        const { userLogs } = rawData;
        return userLogs.map((log) => {
            const state = log.client.geographicalContext.state;
            const stateAbbrev =
                log.client.geographicalContext.country === "United States"
                    ? US_STATES_ABBREV[state]
                    : state;
            const country =
                log.client.geographicalContext.country === "United States"
                    ? ""
                    : `, ${log.client.geographicalContext.country}`;
            const location = `${log.client.geographicalContext.city}, ${stateAbbrev}${country}`;
            return {
                lastLogin: moment(log.published).format("MMM DD, h:mma"),
                location,
                browser: log.client.userAgent.browser,
                os: log.client.userAgent.os,
                ip: log.client.ipAddress
            };
        });
    };

    const fetchActivities = async () => {
        try {
            const { succeed, data } = await getUserLogs();
            setLoading(false);
            if (succeed) {
                const activities = formatActivities(data);
                setActivities(activities);
            }
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchActivities();
    }, []);

    if (loading) {
        return (
            <Card title={t("title")}>
                <SubTitle>
                    { /* t("subtitle") */ }
                         <Trans i18nKey="recentActivities:subtitle">
                            <Link to="/security" /><a href="/contact-us">Customer Service</a>
                        </Trans>
                </SubTitle>
                <CustomGrid>
                    <Loading />
                    <Image src={Logs} alt="notification emails" />
                </CustomGrid>
            </Card>
        );
    }

    return (
        <Card title={t("title")}>
            <SubTitle>
                { /* t("subtitle") */ }
                    <Trans i18nKey="recentActivities:subtitle">
                        <Link to="/security" /><a href="/contact-us">Customer Service</a>
                    </Trans>
            </SubTitle>
            <CustomGrid>
                <Table>
                    <Row>
                        <Header variant="caption" bold>
                            Date
                        </Header>
                        <Header variant="caption" bold>
                            Location
                        </Header>
                        <Header variant="caption" bold>
                            IP Address
                        </Header>
                        <Header variant="caption" bold>
                            Browser
                        </Header>
                        <Header variant="caption" bold>
                            OS
                        </Header>
                    </Row>
                    <Row content>
                        {activities.map((log) => {
                            return (
                                <>
                                    <Cell variant="caption">
                                        {log.lastLogin}
                                    </Cell>
                                    <Cell variant="caption">
                                        Near {log.location}
                                    </Cell>
                                    <Cell variant="caption">{log.ip}</Cell>
                                    <Cell variant="caption">{log.browser}</Cell>
                                    <Cell variant="caption">{log.os}</Cell>
                                </>
                            );
                        })}
                    </Row>
                </Table>
                <Image src={Logs} alt="notification emails" />
            </CustomGrid>
        </Card>
    );
};

export default RecentActivities;
